// Tournaments: table assignment and re-seating across rounds.
//
// Phase 1 of docs/plan-tournaments.md. A tournament IS an event -- one `events`
// row with rounds inside it -- and registration is that event's own RSVP, with
// the round-one roster coming from `confirmed_dayof` AND `status = 'yes'`.
//
// What lives HERE is the pure part: seat naming, the table arithmetic, and the
// validation the server runs before writing a seating plan. `web/` has no test
// runner, so any rule fiddly enough to get wrong belongs on this side of the
// line -- same reasoning as `orderPinnableEvents()` and `hookCanFire()`.
//
// Phase 2 added the draw at the bottom of this file: `drawRound()` seats a round
// against the rounds already played, so the organizer no longer fills 25 tables
// from a <select> each time. Read the block above it before touching it -- it
// records why the draw is per-round rather than the whole-day chart the plan
// recommended, and why that resolved the plan's open question rather than
// ducking it.
//
// Phase 4 added scoring: `scoringProblem()` validates a round's entry and
// `standingsFrom()` turns seats into a table of who is winning. Both pure, both
// at the bottom of this file.
//
// ⚠ The draw still optimises for VARIETY rather than for standing, and that is
// now a CHOICE rather than a limitation -- scores exist, so pairing by standing
// has become buildable. It is deliberately not built: see the block above
// `drawRound()` for why a per-round variety draw suits a roster that changes
// during the event, and §7 of the plan for the question that would have to be
// settled with a player first.
export const DEFAULT_SEATS_PER_TABLE = 4;
/**
 * The four seat winds, in play order.
 *
 * Mahjong seats are named by wind rather than numbered, and the order is
 * meaningful to the game rather than decorative -- which is why `seat_no` is
 * unique per table in the schema. Seat 1 is East, the dealer's seat.
 *
 * Only correct at four seats. `seatLabel()` falls back to a plain number for any
 * other table size, because a five-seat table has no fifth wind and inventing
 * one would be worse than not naming it.
 */
export const SEAT_WINDS = ['East', 'South', 'West', 'North'];
/**
 * What to call seat `seatNo` (1-based) at a table of `seatsPerTable`.
 *
 * Winds at four, "Seat N" otherwise. This is the string a player is shown, so it
 * has to be right for the game and honest when it cannot be.
 */
export function seatLabel(seatNo, seatsPerTable = DEFAULT_SEATS_PER_TABLE) {
    if (seatsPerTable === SEAT_WINDS.length) {
        const wind = SEAT_WINDS[seatNo - 1];
        if (wind !== undefined)
            return wind;
    }
    return `Seat ${seatNo}`;
}
/**
 * How many tables a roster needs, and what does not divide.
 *
 * ⚠ The remainder is the honest part. 100 players make 25 clean tables of four;
 * 25 players make 6 tables plus 1 person over, and that shows up in round two of
 * the very first tournament rather than as some far-off edge case. Mahjong is
 * specifically bad at three or five players -- the game is four-handed, so
 * padding a table changes the game rather than just the arithmetic.
 *
 * This function does NOT decide what to do about it. It reports the shape so the
 * organizer can see it at setup, which in Phase 1 is exactly right: they are
 * seating by hand and are the ones who know whether to run a short table, sit
 * somebody out, or wait for one more person to arrive.
 */
export function tablesNeeded(playerCount, seatsPerTable = DEFAULT_SEATS_PER_TABLE) {
    if (seatsPerTable < 1 || playerCount < 1)
        return { tables: 0, remainder: 0, emptySeats: 0 };
    const full = Math.floor(playerCount / seatsPerTable);
    const remainder = playerCount % seatsPerTable;
    const tables = remainder === 0 ? full : full + 1;
    return {
        tables,
        remainder,
        emptySeats: remainder === 0 ? 0 : seatsPerTable - remainder,
    };
}
/**
 * Why a seating plan is invalid, or null when it is fine.
 *
 * Run server-side before any write. The schema enforces the two rules that must
 * never break -- one player per round, one player per wind -- but a UNIQUE
 * violation surfaces as ER_DUP_ENTRY, which is a 500-shaped answer to what is
 * really a 400. This turns the common mistakes into a sentence naming the
 * player, which is what an organizer with a room full of people needs.
 *
 * The database is still the backstop, deliberately: this function can be
 * bypassed by the next endpoint somebody writes, and the constraints cannot.
 */
export function seatingProblem(plan, roster, seatsPerTable = DEFAULT_SEATS_PER_TABLE) {
    // ⚠ Named, not numbered. This message is read by an organizer standing in a
    // room with people waiting, so "Player1 One is seated more than once" is
    // actionable and "Player 28 is seated more than once" is not -- 28 is an
    // internal id they have no way to resolve. The roster is passed as entries
    // rather than ids for that one reason. Caught by reading the real message on
    // screen rather than the assertion, which was perfectly happy with the id.
    const names = new Map(roster.map((p) => [p.playerId, p.displayName]));
    const who = (id) => names.get(id) ?? `Player ${id}`;
    const seenPlayers = new Set();
    const seenSeats = new Set();
    for (const seat of plan) {
        if (!Number.isInteger(seat.tableNo) || seat.tableNo < 1) {
            return `Table numbers start at 1 (got ${seat.tableNo}).`;
        }
        if (!Number.isInteger(seat.seatNo) || seat.seatNo < 1 || seat.seatNo > seatsPerTable) {
            return `Seat numbers run 1 to ${seatsPerTable} (got ${seat.seatNo} at table ${seat.tableNo}).`;
        }
        if (!names.has(seat.playerId)) {
            // No name to give: an id that is not on the roster is by definition one
            // this function cannot resolve, so the bare number is the honest answer.
            return `Player ${seat.playerId} is not on this tournament's roster.`;
        }
        if (seenPlayers.has(seat.playerId)) {
            // The rule the schema's UNIQUE (round_id, player_id) also guards. Caught
            // here so the organizer gets a sentence rather than a database error.
            return `${who(seat.playerId)} is seated more than once in this round.`;
        }
        const key = `${seat.tableNo}:${seat.seatNo}`;
        if (seenSeats.has(key)) {
            return `Two players are assigned to ${seatLabel(seat.seatNo, seatsPerTable)} at table ${seat.tableNo}.`;
        }
        seenPlayers.add(seat.playerId);
        seenSeats.add(key);
    }
    return null;
}
/**
 * How a roster divides once byes are the mechanism rather than a short table.
 *
 * ⚠ This is NOT `tablesNeeded()`, and the difference is the whole of §7's
 * remainder problem. `tablesNeeded()` reports a last table with empty seats,
 * which is the right answer when an organizer is seating by hand and may decide
 * to run a table of three. The DRAW cannot make that decision for them:
 * mahjong is four-handed, so a padded table changes the game. It sits the
 * remainder out instead, fairly, and says who.
 */
export function drawShape(playerCount, seatsPerTable = DEFAULT_SEATS_PER_TABLE) {
    if (seatsPerTable < 2 || playerCount < seatsPerTable) {
        return { tables: 0, seated: 0, byes: Math.max(playerCount, 0) };
    }
    const tables = Math.floor(playerCount / seatsPerTable);
    const seated = tables * seatsPerTable;
    return { tables, seated, byes: playerCount - seated };
}
/** Order-independent key for a pair of players. */
function pairKey(a, b) {
    return a < b ? `${a}:${b}` : `${b}:${a}`;
}
/** Fisher-Yates, with the rng passed in so a draw can be reproduced. */
function shuffled(items, rng) {
    const out = [...items];
    for (let i = out.length - 1; i > 0; i -= 1) {
        const j = Math.floor(rng() * (i + 1));
        const a = out[i];
        const b = out[j];
        // `noUncheckedIndexedAccess` is on and this is a real guard rather than a
        // formality: an rng returning 1 would index off the end.
        if (a === undefined || b === undefined)
            continue;
        out[i] = b;
        out[j] = a;
    }
    return out;
}
/** How many times each pair of players has already shared a table. */
function meetingCounts(history) {
    const met = new Map();
    for (const round of history) {
        const byTable = new Map();
        for (const seat of round.seats) {
            const list = byTable.get(seat.tableNo);
            if (list)
                list.push(seat.playerId);
            else
                byTable.set(seat.tableNo, [seat.playerId]);
        }
        for (const ids of byTable.values()) {
            for (let i = 0; i < ids.length; i += 1) {
                for (let j = i + 1; j < ids.length; j += 1) {
                    const a = ids[i];
                    const b = ids[j];
                    if (a === undefined || b === undefined)
                        continue;
                    const key = pairKey(a, b);
                    met.set(key, (met.get(key) ?? 0) + 1);
                }
            }
        }
    }
    return met;
}
/**
 * How many times each player has held each seat number.
 *
 * Seat 1 is East, the dealer, which is a real advantage rather than a label --
 * so a draw that hands the same person East four times running is unfair in a
 * way the table assignment alone cannot express.
 */
function seatCounts(history) {
    const seen = new Map();
    for (const round of history) {
        for (const seat of round.seats) {
            const key = `${seat.playerId}:${seat.seatNo}`;
            seen.set(key, (seen.get(key) ?? 0) + 1);
        }
    }
    return seen;
}
/** Prior meetings among everyone at one table -- the thing the draw minimises. */
function tableCost(ids, met) {
    let cost = 0;
    for (let i = 0; i < ids.length; i += 1) {
        for (let j = i + 1; j < ids.length; j += 1) {
            const a = ids[i];
            const b = ids[j];
            if (a === undefined || b === undefined)
                continue;
            cost += met.get(pairKey(a, b)) ?? 0;
        }
    }
    return cost;
}
/**
 * Swap players between tables while it reduces repeat pairings.
 *
 * ⚠ Not a flourish: the greedy fill alone left 2 repeat pairings in a
 * 100-player, 4-round tournament that has 99 possible opponents per player and
 * therefore has no excuse for any. Greedy always does this -- it takes the best
 * table first and hands the last table whoever is left -- and the last table is
 * exactly where the organizer will notice, because it is the same handful of
 * people every round. Ten lines of local search takes it to zero.
 *
 * Bounded on purpose. It is a hill climb, not a solver: it stops when no single
 * swap helps, which is a local minimum and not a promise of the global one.
 * `repeatPairs` is still reported for that reason.
 */
function improveTables(groups, met) {
    // 25 tables is 300 table pairs and 16 swaps each; a handful of passes is
    // nothing. The guard is what stops a pathological history looping.
    for (let pass = 0; pass < 8; pass += 1) {
        let improved = false;
        for (let a = 0; a < groups.length; a += 1) {
            for (let b = a + 1; b < groups.length; b += 1) {
                const ga = groups[a];
                const gb = groups[b];
                if (ga === undefined || gb === undefined)
                    continue;
                for (let i = 0; i < ga.length; i += 1) {
                    for (let j = 0; j < gb.length; j += 1) {
                        const before = tableCost(ga, met) + tableCost(gb, met);
                        const x = ga[i];
                        const y = gb[j];
                        if (x === undefined || y === undefined)
                            continue;
                        ga[i] = y;
                        gb[j] = x;
                        if (tableCost(ga, met) + tableCost(gb, met) < before)
                            improved = true;
                        else {
                            ga[i] = x;
                            gb[j] = y;
                        }
                    }
                }
            }
        }
        if (!improved)
            return;
    }
}
/**
 * Which wind each player at one table takes, minimising repeats exactly.
 *
 * ⚠ Assigning each seat greedily to whoever has held it least does NOT rotate
 * the winds, and the failure is quiet: over four rounds a player would take
 * East twice and never North, because the seat that would have suited them was
 * already given away to somebody equally unburdened. It is a 4x4 assignment
 * problem, and at four seats there are only 24 possibilities -- so try them
 * all. Above six seats that stops being cheap and the greedy is the fallback,
 * which is safe because winds only exist at four in the first place.
 */
function assignWinds(group, seen) {
    const cost = (playerId, seatNo) => seen.get(`${playerId}:${seatNo}`) ?? 0;
    if (group.length <= 6) {
        let best = null;
        let bestCost = Number.POSITIVE_INFINITY;
        const order = [];
        const used = new Array(group.length).fill(false);
        const walk = (running) => {
            if (running >= bestCost)
                return;
            if (order.length === group.length) {
                best = [...order];
                bestCost = running;
                return;
            }
            const seatNo = order.length + 1;
            for (let i = 0; i < group.length; i += 1) {
                if (used[i] === true)
                    continue;
                const id = group[i];
                if (id === undefined)
                    continue;
                used[i] = true;
                order.push(id);
                walk(running + cost(id, seatNo));
                order.pop();
                used[i] = false;
            }
        };
        walk(0);
        // The group order is already shuffled, so the first permutation reaching the
        // minimum is a random pick among equals rather than always the same one.
        const chosen = best ?? group;
        return chosen.map((playerId, i) => ({ playerId, seatNo: i + 1 }));
    }
    const left = [...group];
    const out = [];
    for (let seatNo = 1; left.length; seatNo += 1) {
        let bestIndex = 0;
        let bestCount = Number.POSITIVE_INFINITY;
        for (let i = 0; i < left.length; i += 1) {
            const cand = left[i];
            if (cand === undefined)
                continue;
            const c = cost(cand, seatNo);
            if (c < bestCount) {
                bestCount = c;
                bestIndex = i;
            }
        }
        const taker = left.splice(bestIndex, 1)[0];
        if (taker === undefined)
            break;
        out.push({ playerId: taker, seatNo });
    }
    return out;
}
/**
 * Draw one round: who sits out, who shares a table, and which wind each takes.
 *
 * Pure, and here rather than in `web/` for the usual reason -- `web/` has no
 * test runner and this is much too fiddly to leave unasserted.
 *
 * Three rules, in the order they are applied:
 *
 * 1. **Byes go to whoever has sat out least.** That is the fairness rule §7
 *    asked for, and it makes "never the same player twice before everyone has
 *    had one" a property of the sort rather than a special case. Ties are broken
 *    by the rng, which is why the roster is shuffled BEFORE the sort: `sort` is
 *    stable in every engine since ES2019, so a shuffle followed by a stable
 *    sort on bye count is a random tiebreak.
 * 2. **Tables minimise repeat pairings, greedily.** Seed each table with the
 *    next player and fill it with whoever has met the current occupants least.
 *    Best-effort by design; the count comes back in `repeatPairs`.
 * 3. **Winds go to whoever has held them least**, so East rotates.
 *
 * Returns an empty draw -- 0 tables, everybody byed -- when the roster cannot
 * fill a single table. The caller must refuse to SAVE that, or an accidental
 * draw with three players in the room would clear a seating plan that was fine.
 */
export function drawRound(roster, history = [], opts = {}) {
    const seatsPerTable = opts.seatsPerTable ?? DEFAULT_SEATS_PER_TABLE;
    const rng = opts.rng ?? Math.random;
    const shape = drawShape(roster.length, seatsPerTable);
    if (shape.tables === 0) {
        return {
            seats: [],
            byePlayerIds: roster.map((p) => p.playerId),
            tables: 0,
            repeatPairs: 0,
            seatedPairs: 0,
        };
    }
    // Rule 1: fewest byes so far sit out next. Shuffle, then stable-sort.
    const byByes = shuffled(roster, rng).sort((a, b) => (a.byes ?? 0) - (b.byes ?? 0));
    const byePlayerIds = byByes.slice(0, shape.byes).map((p) => p.playerId);
    const byed = new Set(byePlayerIds);
    // Reshuffled rather than reusing the bye ordering: that one is sorted by bye
    // count, and seeding tables in bye-count order would correlate the tables
    // with who has sat out, for no reason.
    const remaining = shuffled(roster.filter((p) => !byed.has(p.playerId)), rng);
    const met = meetingCounts(history);
    const seen = seatCounts(history);
    // Rule 2, part one: fill each table with whoever has met it least. Greedy,
    // and on its own not good enough -- see improveTables() below.
    const groups = [];
    for (let tableNo = 1; tableNo <= shape.tables; tableNo += 1) {
        const seed = remaining.shift();
        if (seed === undefined)
            break;
        const group = [seed.playerId];
        while (group.length < seatsPerTable) {
            let bestIndex = -1;
            let bestCost = Number.POSITIVE_INFINITY;
            for (let i = 0; i < remaining.length; i += 1) {
                const cand = remaining[i];
                if (cand === undefined)
                    continue;
                let cost = 0;
                for (const id of group)
                    cost += met.get(pairKey(cand.playerId, id)) ?? 0;
                if (cost < bestCost) {
                    bestCost = cost;
                    bestIndex = i;
                    // A stranger to everyone at the table is as good as it gets, and
                    // taking the first one keeps the draw cheap at 25 tables.
                    if (cost === 0)
                        break;
                }
            }
            if (bestIndex < 0)
                break;
            const picked = remaining.splice(bestIndex, 1)[0];
            if (picked === undefined)
                break;
            group.push(picked.playerId);
        }
        groups.push(group);
    }
    // Rule 2, part two: swap between tables while it helps. This is what takes a
    // 100-player draw from "two avoidable repeats" to none.
    improveTables(groups, met);
    const seats = [];
    let repeatPairs = 0;
    let seatedPairs = 0;
    for (let i = 0; i < groups.length; i += 1) {
        const group = groups[i];
        if (group === undefined)
            continue;
        const tableNo = i + 1;
        for (let a = 0; a < group.length; a += 1) {
            for (let b = a + 1; b < group.length; b += 1) {
                const x = group[a];
                const y = group[b];
                if (x === undefined || y === undefined)
                    continue;
                seatedPairs += 1;
                if ((met.get(pairKey(x, y)) ?? 0) > 0)
                    repeatPairs += 1;
            }
        }
        // Rule 3: winds to whoever has held them least, solved exactly per table.
        for (const seat of assignWinds(group, seen)) {
            seats.push({ playerId: seat.playerId, tableNo, seatNo: seat.seatNo });
        }
    }
    return { seats, byePlayerIds, tables: shape.tables, repeatPairs, seatedPairs };
}
/**
 * Why a round's scores are invalid, or null when they are fine.
 *
 * Server-side before any write, and worded for an organizer standing in a room
 * -- so it names the player or the table, never an id. Same contract as
 * `seatingProblem()`, including that the message is shown verbatim.
 *
 * `tableScoreTotal` is the variant-dependent check: when set, each table's
 * scores must sum to exactly it, and the complaint says by how much they miss.
 * That is the whole of what "which mahjong" decides.
 */
export function scoringProblem(scores, seating, roster, tableScoreTotal = null) {
    const names = new Map(roster.map((p) => [p.playerId, p.displayName]));
    const who = (id) => names.get(id) ?? `Player ${id}`;
    const seat = new Map(seating.map((s) => [s.playerId, s]));
    const seen = new Set();
    for (const entry of scores) {
        if (!Number.isInteger(entry.score)) {
            // Riichi deltas are whole point sticks and poker points are whole points.
            // A half-point is a typo in every variant.
            return `${who(entry.playerId)}'s score must be a whole number (got ${entry.score}).`;
        }
        // Wide, but bounded: an int column overflows and a mistyped run of digits
        // should be refused by name rather than by a database error.
        if (Math.abs(entry.score) > 100_000_000) {
            return `${who(entry.playerId)}'s score is out of range.`;
        }
        if (!seat.has(entry.playerId)) {
            // Covers both an off-roster id and, more usefully, a player who sat this
            // round out: a bye has no seat, so it cannot have a score.
            return `${who(entry.playerId)} is not seated in this round, so cannot be scored.`;
        }
        if (seen.has(entry.playerId)) {
            return `${who(entry.playerId)} has been given two scores.`;
        }
        seen.add(entry.playerId);
    }
    // ⚠ PARTIAL ENTRY IS ALLOWED, deliberately. An organizer walks the room
    // typing one table at a time, and refusing a save until all 25 are in would
    // mean holding minutes of work in a browser tab -- which is the data-loss
    // trap the Phase 1 poll had. Completeness is checked when the round is
    // CLOSED, not when it is saved. So this validates what was sent, and
    // `unscoredSeats()` is what the close path asks.
    if (tableScoreTotal !== null) {
        const byTable = new Map();
        for (const entry of scores) {
            const s = seat.get(entry.playerId);
            if (s === undefined)
                continue;
            const list = byTable.get(s.tableNo);
            if (list)
                list.push(entry);
            else
                byTable.set(s.tableNo, [entry]);
        }
        for (const [tableNo, entries] of byTable) {
            const seatsAtTable = seating.filter((s) => s.tableNo === tableNo).length;
            // Only a COMPLETE table can be checked against a total. A half-typed
            // table is not wrong yet, and complaining about it while the organizer
            // is still typing would make the check unusable.
            if (entries.length < seatsAtTable)
                continue;
            const sum = entries.reduce((total, e) => total + e.score, 0);
            if (sum !== tableScoreTotal) {
                const off = sum - tableScoreTotal;
                return (`Table ${tableNo} adds up to ${sum}, not ${tableScoreTotal} ` +
                    `(${off > 0 ? 'over' : 'under'} by ${Math.abs(off)}). Somebody's score is wrong.`);
            }
        }
    }
    return null;
}
/**
 * Seated players in this round with no score yet.
 *
 * The close path's question, and the reason `score` is nullable rather than
 * defaulting to 0: "nobody has entered this" and "everybody drew zero" are
 * different facts, and only the first should block a round from closing.
 */
export function unscoredSeats(seating, scores) {
    const scored = new Set(scores.map((s) => s.playerId));
    return seating.filter((s) => !scored.has(s.playerId)).map((s) => s.playerId);
}
/**
 * The standings, best first.
 *
 * ⚠ A BYE SCORES NOTHING, AND WHETHER THAT IS FAIR DEPENDS ON THE VARIANT.
 * With a zero-sum ruleset (`tableScoreTotal` set, riichi's 100,000) a round's
 * scores average to a quarter of the total and a player sitting out neither
 * gains nor loses relative to the field -- so nothing is needed. Without one
 * (poker points, where every score is positive) a bye IS a real disadvantage,
 * because everybody else banked points that round and the byed player could
 * not. This does not paper over that: `roundsPlayed` is on every row so the
 * comparison is visible, and the honest fix -- awarding a bye the field's
 * average, or the player's own -- is a rule for a real tournament to argue for
 * rather than one to invent here. Recorded in the plan as a judgment call.
 *
 * Withdrawn players stay in the table. They played those rounds and the scores
 * of the people they played against depend on it; deleting them from the
 * standings would rewrite somebody else's afternoon. They are just never the
 * winner.
 */
export function standingsFrom(players, seating) {
    const totals = new Map();
    for (const round of seating) {
        for (const seat of round.seats) {
            if (seat.score === null || seat.score === undefined)
                continue;
            const acc = totals.get(seat.playerId) ?? { total: 0, rounds: 0 };
            acc.total += seat.score;
            acc.rounds += 1;
            totals.set(seat.playerId, acc);
        }
    }
    const rows = players
        .map((p) => {
        const acc = totals.get(p.playerId) ?? { total: 0, rounds: 0 };
        return {
            playerId: p.playerId,
            displayName: p.displayName,
            total: acc.total,
            roundsPlayed: acc.rounds,
            rank: 0,
            withdrawn: p.withdrawn === true,
        };
    })
        // Highest total first, then the name -- a stable, alphabetical order among
        // equals rather than whatever the database happened to return, so the table
        // does not reshuffle itself between two page loads that mean the same thing.
        .sort((a, b) => b.total - a.total || a.displayName.localeCompare(b.displayName));
    let rank = 0;
    let previous = null;
    rows.forEach((row, i) => {
        if (previous === null || row.total !== previous)
            rank = i + 1;
        row.rank = rank;
        previous = row.total;
    });
    return rows;
}
/**
 * Who won, or the tie that has to be broken first.
 *
 * ⚠ IT REFUSES TO GUESS. A tournament's whole point is one winner, so the
 * tempting thing is to return the first row -- which would silently award the
 * title alphabetically. Two players level at the top is a real outcome that a
 * room resolves with a play-off or a tiebreak the app knows nothing about, so
 * the honest answer is to name both and let the organizer say.
 *
 * A player who has played no scored round is not a winner either, which is what
 * stops an unscored tournament crowning whoever sorts first on a total of zero.
 */
export function tournamentWinner(standings) {
    const eligible = standings.filter((s) => !s.withdrawn && s.roundsPlayed > 0);
    const best = eligible[0];
    if (best === undefined)
        return { winnerPlayerId: null, tiedForFirst: [] };
    const top = eligible.filter((s) => s.total === best.total);
    if (top.length === 1)
        return { winnerPlayerId: best.playerId, tiedForFirst: [] };
    return { winnerPlayerId: null, tiedForFirst: top.map((s) => s.playerId) };
}
/** A position as one comparable string. 'bye' for a player with no seat. */
function positionKey(p) {
    if (p.tableNo === null || p.seatNo === null)
        return 'bye';
    return `${p.tableNo}:${p.seatNo}`;
}
/**
 * Players whose position CHANGED, and therefore the only ones worth a push.
 *
 * ⚠ THIS IS WHY A RE-DRAW DOES NOT SPAM THE ROOM. An organizer re-draws a round
 * two or three times while looking at the repeat-pairing count, and pushing on
 * every write would buzz a hundred phones each time -- with most of those
 * notifications saying exactly what the previous one said. Worse than noisy: a
 * player learns to ignore the one that finally matters.
 *
 * So the push list is a diff, not the roster. A player who ends up at the same
 * table in the same wind hears nothing; a player who moves, or who newly starts
 * or stops sitting out, hears once.
 *
 * A round that was UNDRAWN before has everybody on 'bye', so a first draw
 * correctly tells everybody -- the diff needs no special case for it.
 *
 * Players only in `before` are ignored: they have left the round (withdrawn, or
 * the roster changed), and telling somebody about a round they are no longer in
 * is worse than telling them nothing.
 */
export function movedPlayers(before, after) {
    const was = new Map(before.map((p) => [p.playerId, positionKey(p)]));
    const moved = [];
    for (const now of after) {
        // A player with no `before` entry at all is new to the round, which is a
        // change by definition -- and defaulting them to 'bye' rather than to
        // "unchanged" is what makes a first draw notify everybody.
        if ((was.get(now.playerId) ?? 'bye') !== positionKey(now))
            moved.push(now.playerId);
    }
    return moved;
}
//# sourceMappingURL=tournaments.js.map